// ============================================================
// FIXED: CustomerPurchaseHistoryPage.tsx
// ============================================================
// src/features/reports/pages/CustomerPurchaseHistoryPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { getCustomerPurchaseHistory, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    History, ShoppingBag, DollarSign, Calendar,
    TrendingUp, Package, Search, Heart,
    ChevronLeft, ChevronRight, User, Phone
} from 'lucide-react';
import { motion } from 'framer-motion';

export function CustomerPurchaseHistoryPage() {
    const userAuth = useAuthStore((s) => s.user);
    const tenantId = userAuth?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [expandedSale, setExpandedSale] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const limit = 20;

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        limit: limit,
        page: page,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-customer-purchase-history', tenantId, params],
        queryFn: () => getCustomerPurchaseHistory(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'customer_purchase_history', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || searchTerm);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setSearchTerm(''); setPage(1); };

    const purchases = data?.purchases || [];
    const pagination = data?.pagination;

    const filteredPurchases = searchTerm
        ? purchases.filter((p: any) =>
            p.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.customerName?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : purchases;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
            <ReportHeader
                title="Customer Purchase History"
                description="All customer transactions"
                icon={History}
                iconGradient="from-blue-500 to-indigo-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId=""
                    branches={[]}
                    onDateRangeChange={setDateRange}
                    onBranchChange={() => { }}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                    additionalFilters={
                        <div className="flex items-center gap-2">
                            <Search size={14} className="text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by customer name or order #"
                                value={searchTerm}
                                onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                                className="px-3 py-1.5 border rounded-lg text-xs w-64"
                            />
                        </div>
                    }
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || filteredPurchases.length === 0 ? (
                    <EmptyState
                        icon={<History className="w-10 h-10" />}
                        title="No Purchase History"
                        description={searchTerm ? "No purchases match your search." : "No purchases found for the selected period."}
                        size="lg"
                        action={searchTerm ? { label: 'Clear Search', onClick: clearFilters } : undefined}
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <SummaryCard
                                label="Total Purchases"
                                value={String(data.summary?.totalPurchases || 0)}
                                icon={ShoppingBag}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <SummaryCard
                                label="Total Revenue"
                                value={formatNaira(data.summary?.totalSpent || 0)}
                                icon={DollarSign}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Avg Purchase"
                                value={formatNaira(data.summary?.avgPurchaseValue || 0)}
                                icon={TrendingUp}
                                gradient="from-purple-500 to-violet-600"
                            />
                            <SummaryCard
                                label="Page"
                                value={`${pagination?.page || 1} of ${pagination?.totalPages || 1}`}
                                icon={Package}
                                gradient="from-rose-500 to-pink-600"
                                subtitle={`${pagination?.totalCount || 0} total`}
                            />
                        </div>

                        <div className="space-y-3">
                            {filteredPurchases.map((purchase: any, i: number) => {
                                const isExpanded = expandedSale === purchase.id;

                                return (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.02 }}
                                    >
                                        <Card hover className="cursor-pointer" onClick={() => setExpandedSale(isExpanded ? null : purchase.id)}>
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                                                            <ShoppingBag className="w-5 h-5 text-blue-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-bold text-slate-900">{purchase.orderNumber}</p>
                                                            <div className="flex items-center gap-3 text-xs text-slate-500">
                                                                <span className="flex items-center gap-1">
                                                                    <User size={12} />
                                                                    {purchase.customerName || 'Walk-in Customer'}
                                                                </span>
                                                                {purchase.customerPhone && (
                                                                    <span className="flex items-center gap-1">
                                                                        <Phone size={12} />
                                                                        {purchase.customerPhone}
                                                                    </span>
                                                                )}
                                                                <span className="flex items-center gap-1">
                                                                    <Calendar size={12} />
                                                                    {new Date(purchase.createdAt).toLocaleDateString('en-NG', {
                                                                        day: 'numeric',
                                                                        month: 'short',
                                                                        year: 'numeric',
                                                                        hour: '2-digit',
                                                                        minute: '2-digit',
                                                                    })}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-lg font-bold text-slate-900">{formatNaira(purchase.grandTotal)}</p>
                                                        <p className="text-xs text-slate-400">
                                                            {purchase.items?.length || 0} items
                                                            {parseFloat(purchase.discountTotal) > 0 && (
                                                                <span className="text-amber-600 ml-2">
                                                                    -{formatNaira(purchase.discountTotal)}
                                                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>

                                                {isExpanded && purchase.items && (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        className="mt-4 pt-4 border-t border-slate-100"
                                                    >
                                                        <div className="space-y-2">
                                                            {purchase.items.map((item: any, j: number) => (
                                                                <div key={j} className="flex items-center justify-between text-sm">
                                                                    <div className="flex items-center gap-2">
                                                                        <Package size={14} className="text-slate-400" />
                                                                        <span className="text-slate-700">{item.productName}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-4">
                                                                        <span className="text-slate-500">x{parseFloat(item.quantity).toLocaleString()}</span>
                                                                        <span className="text-slate-500">{formatNaira(item.unitPrice)}</span>
                                                                        <span className="font-semibold text-slate-900">{formatNaira(item.totalAmount)}</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between text-sm">
                                                            <span className="text-slate-500">Tax: {formatNaira(purchase.taxTotal)}</span>
                                                            <span className="text-slate-500">Discount: {formatNaira(purchase.discountTotal)}</span>
                                                            <span className="font-bold text-slate-900">Total: {formatNaira(purchase.grandTotal)}</span>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {pagination && pagination.totalPages > 1 && (
                            <div className="flex items-center justify-between pt-4">
                                <p className="text-sm text-slate-500">
                                    Showing {((pagination.page - 1) * limit) + 1}-
                                    {Math.min(pagination.page * limit, pagination.totalCount)} of {pagination.totalCount}
                                </p>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        disabled={!pagination.hasPrevPage}
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        leftIcon={<ChevronLeft size={14} />}
                                    >
                                        Previous
                                    </Button>
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum: number;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (page <= 3) {
                                            pageNum = i + 1;
                                        } else if (page >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = page - 2 + i;
                                        }
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setPage(pageNum)}
                                                className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${pageNum === page
                                                        ? 'bg-blue-500 text-white'
                                                        : 'text-slate-600 hover:bg-slate-100'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        disabled={!pagination.hasNextPage}
                                        onClick={() => setPage(p => p + 1)}
                                        rightIcon={<ChevronRight size={14} />}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}